import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PersonalRecordsForm = ({ employee, onSave, loading }) => {
  const [formData, setFormData] = useState({
    phone: '',
    email: ''
  });

  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (employee) {
      setFormData({
        phone: employee?.phone || '',
        email: employee?.email || ''
      });
    }
  }, [employee]);

  useEffect(() => {
    const changed = 
      formData?.phone !== employee?.phone ||
      formData?.email !== employee?.email;
    setHasChanges(changed);
  }, [formData, employee]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9+\-\s()]+$/;
    return phone?.length >= 7 && phoneRegex?.test(phone);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData?.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave?.(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Info" size={16} className="text-primary" />
          <p className="text-sm text-muted-foreground">
            You can update your contact information below. Changes require approval from HR.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Employee Number
          </label>
          <Input
            value={employee?.employeeNumber || ''}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Full Name
          </label>
          <Input
            value={`${employee?.firstName || ''} ${employee?.lastName || ''}`}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Department
          </label>
          <Input
            value={employee?.department || ''}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Position
          </label>
          <Input
            value={employee?.position || ''}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Email Address *
          </label>
          <Input
            type="email"
            value={formData?.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e?.target?.value });
              setErrors({ ...errors, email: '' });
            }}
            error={errors?.email}
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Phone Number *
          </label>
          <Input
            type="tel"
            value={formData?.phone}
            onChange={(e) => {
              setFormData({ ...formData, phone: e?.target?.value });
              setErrors({ ...errors, phone: '' });
            }}
            error={errors?.phone}
            placeholder="+675 XXX XXXX"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Hire Date
          </label>
          <Input
            value={employee?.hireDate ? new Date(employee?.hireDate)?.toLocaleDateString() : ''}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Employment Type
          </label>
          <Input
            value={employee?.employmentType || ''}
            disabled
            className="bg-muted"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={loading || !hasChanges}
          className="flex-1"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
        {hasChanges && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFormData({
                phone: employee?.phone || '',
                email: employee?.email || ''
              });
              setErrors({});
            }}
            disabled={loading}
          >
            Reset
          </Button>
        )}
      </div>
    </form>
  );
};

export default PersonalRecordsForm;
